// Phase 5Z.25 — Bulk-select hook + checkbox row + bulk-action toolbar
import { useState, useMemo } from "react";
import { Trash2, X } from "lucide-react";

/**
 * useBulkSelect(items, idKey="id") → { selected, isSelected, toggle, toggleAll, clear, allSelected, someSelected, selectedCount, selectedIds }
 */
export function useBulkSelect(items, idKey = "id") {
  const [selected, setSelected] = useState(new Set());

  const allIds = useMemo(() => items.map((it) => it[idKey]).filter(Boolean), [items, idKey]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = !allSelected && allIds.some((id) => selected.has(id));
  const selectedIds = useMemo(() => Array.from(selected).filter((id) => allIds.includes(id)), [selected, allIds]);

  function toggle(id) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function toggleAll() {
    setSelected((s) => {
      if (allIds.every((id) => s.has(id))) return new Set();
      const n = new Set(s);
      allIds.forEach((id) => n.add(id));
      return n;
    });
  }
  function clear() { setSelected(new Set()); }
  function isSelected(id) { return selected.has(id); }

  return {
    selected, isSelected, toggle, toggleAll, clear,
    allSelected, someSelected,
    selectedCount: selectedIds.length, selectedIds,
  };
}

/** <BulkCheckbox checked={bool} indeterminate={bool} onChange={fn} testid="..." /> */
export function BulkCheckbox({ checked, indeterminate, onChange, disabled, testid }) {
  return (
    <input
      type="checkbox"
      checked={!!checked}
      ref={(el) => { if (el) el.indeterminate = !!indeterminate; }}
      onChange={onChange}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
      className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-amber-500 focus:ring-amber-500 focus:ring-offset-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
    />
  );
}

/**
 * <BulkActionBar count onClear onDelete deleteLabel="Auswahl löschen" />
 * Sticky toolbar that appears above the table when ≥ 1 row is selected.
 */
export function BulkActionBar({ count, onClear, onDelete, deleteLabel = "Auswahl löschen", testid = "bulk-action-bar" }) {
  if (!count) return null;
  return (
    <div
      data-testid={testid}
      className="sticky top-0 z-10 mb-2 flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-500/10 border border-amber-500/40 rounded-lg backdrop-blur-sm"
    >
      <div className="flex items-center gap-3">
        <button
          onClick={onClear}
          data-testid={`${testid}-clear`}
          className="text-amber-300 hover:text-amber-100 p-1 rounded"
          aria-label="Auswahl aufheben"
        >
          <X className="w-4 h-4" />
        </button>
        <span className="text-sm font-medium text-amber-200" data-testid={`${testid}-count`}>
          {count} ausgewählt
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onDelete}
          data-testid={`${testid}-delete`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-500 text-white font-medium transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          {deleteLabel}
        </button>
      </div>
    </div>
  );
}
